import { motion, useReducedMotion } from 'motion/react';

import DefaultLink from 'components/common/DefaultLink';
import ScrollableList from 'components/common/ScrollableList';
import IngredientsArrangedOnGreenBackground from 'images/familyHome/ingredients-arranged-on-green-background.jpg';
import KidEatingPizza from 'images/familyHome/kid-eating-pizza.jpg';
import SheetpanChickenTablescape from 'images/familyHome/sheetpan-chicken-tablescape.jpg';
import { logEvent, events } from 'services/analytics';

const PARENT_VARIANT = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const createSlideInVariants = prefersReducedMotion => ({
  hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 100 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      type: prefersReducedMotion ? 'tween' : 'spring',
    },
  },
});

const ValuePropCard = ({ imageSrc, imageAlt, title, description, buttonText, buttonLink }) => {
  const prefersReducedMotion = useReducedMotion();
  const slideInVariants = createSlideInVariants(prefersReducedMotion);

  return (
    <motion.li
      variants={slideInVariants}
      className="flex w-4/5 max-w-7xl shrink-0 flex-col items-stretch rounded-xl bg-white-110 p-4 pb-10 first:ml-4 last:mr-4 bp1:first:ml-8 bp1:last:mr-8 bp2:flex-row bp2:p-6"
    >
      <img
        src={imageSrc}
        alt={imageAlt}
        className="mb-6 w-full rounded-xl object-cover bp2:mb-0 bp2:w-3/5"
      />
      <div className="mx-auto flex max-w-md flex-col items-center bp2:my-auto bp3:px-8">
        <h2 className="h2 bp3:h1 max-w-48 pb-6 text-center text-green-140 bp1:max-w-md">{title}</h2>
        <p className="text pb-8 text-center text-green-140">{description}</p>
        <DefaultLink
          to={buttonLink}
          isExternal={true}
          variation="Primary"
          className="btn btnMd w-auto border-none bg-green-60 text-green-140 hover:bg-green-60 hover:text-green-140 hover:brightness-90"
          onClick={() =>
            logEvent(events.familyHome.submitted, {
              urlPath: buttonLink,
            })
          }
        >
          {buttonText}
        </DefaultLink>
      </div>
    </motion.li>
  );
};

ValuePropCard.propTypes = {
  imageSrc: PropTypes.string.isRequired,
  imageAlt: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
  buttonLink: PropTypes.string.isRequired,
};

const IMAGE_MAP = {
  picky_eater: KidEatingPizza,
  meal_planning: SheetpanChickenTablescape,
  time_in_kitchen: IngredientsArrangedOnGreenBackground,
};

const ValueProps = ({ data }) => {
  const cards = data.map(card => {
    return {
      ...card,
      imageSrc: IMAGE_MAP[card.key],
    };
  });

  return (
    <section className="-mt-px bg-family-beige-100 py-8 bp2:py-20">
      <motion.div
        variants={PARENT_VARIANT}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.5 }}
      >
        <ScrollableList
          numSlides={cards.length}
          showArrows={true}
          buttonStyles="invisible bp1:visible shadow-xl bg-white-110 border-2 border-solid border-green-140 hover:bg-white-110 hover:brightness-90 mx-4 top-1/2 -translate-y-1/2"
          iconStyles="iconLg text-green-140"
          slidesContainerStyles="gap-4"
        >
          {cards.map((card, index) => (
            <ValuePropCard key={index} {...card} />
          ))}
        </ScrollableList>
      </motion.div>
    </section>
  );
};

ValueProps.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      buttonText: PropTypes.string.isRequired,
      buttonLink: PropTypes.string.isRequired,
      imageAlt: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default ValueProps;
